import { useEffect, useRef, useState, useCallback } from 'react'
import { Cast, Airplay, Loader2, X, Volume2, AlertCircle } from 'lucide-react'
import { useTVScene } from '@/hooks/useTVScene'

// OBACast Custom Receiver Application ID
// Set via deploy script: ./scripts/deploy-cast-receiver.sh --app-id YOUR_ID
const OBA_RECEIVER_APP_ID: string = '63BE639A'

// Custom namespace for OBA multiview protocol
const OBA_NAMESPACE = 'urn:x-cast:com.oba.multiview'

// Message types
const MessageType = {
  SCENE_LOAD: 'SCENE_LOAD',
  SCENE_PATCH: 'SCENE_PATCH',
  SET_LAYOUT: 'SET_LAYOUT',
  SET_AUDIO_SLOT: 'SET_AUDIO_SLOT',
  HEARTBEAT: 'HEARTBEAT',
  PING: 'PING',
  PAUSE_ALL: 'PAUSE_ALL',
  RESUME_ALL: 'RESUME_ALL',
  STOP: 'STOP',
} as const

const ResponseType = {
  STATE_UPDATE: 'STATE_UPDATE',
  TILE_STATUS: 'TILE_STATUS',
  PONG: 'PONG',
  ACK: 'ACK',
  ERROR: 'ERROR',
  READY: 'READY',
  FALLBACK_ACTIVATED: 'FALLBACK_ACTIVATED',
} as const

// Cast mode: native (Phase 2) or composite (Phase 1 fallback)
type CastMode = 'native' | 'composite'

// Receiver capabilities
interface ReceiverCapabilities {
  nativeQuad: boolean
  maxTiles: number
}

// Declare Google Cast types
declare global {
  interface Window {
    __onGCastApiAvailable?: (isAvailable: boolean) => void
    chrome?: {
      cast?: {
        media: {
          DEFAULT_MEDIA_RECEIVER_APP_ID: string
          MediaInfo: new (url: string, contentType: string) => chrome.cast.media.MediaInfo
          LoadRequest: new (mediaInfo: chrome.cast.media.MediaInfo) => chrome.cast.media.LoadRequest
        }
        AutoJoinPolicy: {
          ORIGIN_SCOPED: string
        }
      }
    }
    cast?: {
      framework: {
        CastContext: {
          getInstance: () => CastContext
        }
        CastContextEventType: {
          SESSION_STATE_CHANGED: string
          CAST_STATE_CHANGED: string
        }
        SessionState: {
          SESSION_STARTED: string
          SESSION_ENDED: string
          SESSION_RESUMED: string
        }
        CastState: {
          NO_DEVICES_AVAILABLE: string
          NOT_CONNECTED: string
          CONNECTING: string
          CONNECTED: string
        }
      }
    }
  }

  namespace chrome.cast.media {
    interface MediaInfo {
      contentType: string
    }
    interface LoadRequest {
      autoplay: boolean
    }
  }

  interface CastContext {
    setOptions: (options: { receiverApplicationId: string; autoJoinPolicy: string }) => void
    getCurrentSession: () => CastSession | null
    requestSession: () => Promise<void>
    endCurrentSession: (stopCasting: boolean) => void
    addEventListener: (type: string, listener: (event: CastEvent) => void) => void
    removeEventListener: (type: string, listener: (event: CastEvent) => void) => void
    getCastState: () => string
  }

  interface CastSession {
    loadMedia: (request: chrome.cast.media.LoadRequest) => Promise<void>
    getSessionObj: () => { receiver: { friendlyName: string } }
    sendMessage: (namespace: string, message: object) => Promise<void>
    addMessageListener: (namespace: string, listener: (namespace: string, message: string) => void) => void
    removeMessageListener: (namespace: string, listener: (namespace: string, message: string) => void) => void
  }

  interface CastEvent {
    sessionState?: string
    castState?: string
  }
}

interface CastPanelProps {
  className?: string
}

export default function CastPanel({ className = '' }: CastPanelProps) {
  console.log('[CastPanel] Component mounted')

  const {
    castState,
    castSession,
    castError,
    castDeviceName,
    tiles,
    layout,
    activeAudioSlot,
    startCastSession,
    stopCastSession,
    setCastState,
    setCastDeviceName,
    setCastError,
  } = useTVScene()

  const [castAvailable, setCastAvailable] = useState(false)
  const [airPlayAvailable, setAirPlayAvailable] = useState(false)
  const [isAndroid] = useState(() => /Android/i.test(navigator.userAgent))
  const [castMode, setCastMode] = useState<CastMode>('native')
  const [receiverCapabilities, setReceiverCapabilities] = useState<ReceiverCapabilities | null>(null)
  const [_receiverState, setReceiverState] = useState<string | null>(null)

  const videoRef = useRef<HTMLVideoElement>(null)
  const castInitialized = useRef(false)
  const heartbeatTimer = useRef<number | null>(null)
  const messageListenerRef = useRef<((namespace: string, message: string) => void) | null>(null)

  // Initialize Google Cast SDK
  useEffect(() => {
    if (castInitialized.current) return

    console.log('[CastPanel] Initializing Cast SDK...')

    // Check if Cast API is already loaded
    if (window.cast?.framework) {
      console.log('[CastPanel] Cast API already loaded')
      initializeCast()
      return
    }

    // Set up callback for when Cast API loads
    window.__onGCastApiAvailable = (isAvailable: boolean) => {
      console.log('[CastPanel] __onGCastApiAvailable called:', isAvailable)
      console.log('[CastPanel] window.cast:', typeof window.cast)
      console.log('[CastPanel] window.chrome?.cast:', typeof window.chrome?.cast)
      if (isAvailable) {
        initializeCast()
      } else {
        console.warn('[CastPanel] Cast API not available - this is normal on Android Chrome')
      }
    }

    // Load Cast SDK script
    const script = document.createElement('script')
    script.src = 'https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1'
    script.async = true
    script.onload = () => {
      console.log('[CastPanel] Cast SDK script loaded')
      // Fallback: try to initialize after a short delay if callback wasn't called
      setTimeout(() => {
        if (!castInitialized.current && window.cast?.framework) {
          console.log('[CastPanel] Fallback initialization attempt')
          initializeCast()
        }
      }, 2000)
    }
    script.onerror = (e) => console.error('[CastPanel] Cast SDK script failed to load:', e)
    document.head.appendChild(script)

    return () => {
      window.__onGCastApiAvailable = undefined
    }
  }, [])

  // Check for AirPlay support
  useEffect(() => {
    const video = document.createElement('video')
    setAirPlayAvailable('webkitShowPlaybackTargetPicker' in video)
  }, [])

  // Update hidden video source when cast session changes (for AirPlay composite fallback)
  useEffect(() => {
    if (videoRef.current && castSession?.playbackUrl) {
      const fullUrl = `${window.location.origin}${castSession.playbackUrl}`
      videoRef.current.src = fullUrl
    }
  }, [castSession?.playbackUrl])

  // Sync scene changes to receiver when connected
  useEffect(() => {
    if (castState === 'connected' && castMode === 'native') {
      sendScenePatch()
    }
  }, [tiles, layout, activeAudioSlot])

  const initializeCast = () => {
    console.log('[CastPanel] initializeCast called', {
      castFramework: !!window.cast?.framework,
      chromeCast: !!window.chrome?.cast
    })

    if (!window.cast?.framework || !window.chrome?.cast) {
      console.warn('[CastPanel] Cast framework not available')
      return
    }

    castInitialized.current = true

    const castContext = window.cast.framework.CastContext.getInstance()

    // Use custom OBA receiver app (supports multi-view)
    // Falls back to default media receiver if custom app ID not configured
    const appId = OBA_RECEIVER_APP_ID && !OBA_RECEIVER_APP_ID.includes('YOUR_APP')
      ? OBA_RECEIVER_APP_ID
      : window.chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID

    const isCustomReceiver = appId !== window.chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID
    console.log('[CastPanel] Using receiver:', isCustomReceiver ? 'OBACast custom' : 'Default media', 'App ID:', appId)

    try {
      castContext.setOptions({
        receiverApplicationId: appId,
        autoJoinPolicy: window.chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
      })
      console.log('[CastPanel] Cast options set successfully')
    } catch (e) {
      console.error('[CastPanel] Failed to set cast options:', e)
      return
    }

    // Custom receiver = native mode (multi-view), default = composite mode (single stream)
    setCastMode(isCustomReceiver ? 'native' : 'composite')

    // Listen for cast state changes
    castContext.addEventListener(
      window.cast.framework.CastContextEventType.CAST_STATE_CHANGED,
      handleCastStateChange
    )

    castContext.addEventListener(
      window.cast.framework.CastContextEventType.SESSION_STATE_CHANGED,
      handleSessionStateChange
    )

    // Check initial cast state to determine if devices are available
    const initialState = castContext.getCastState()
    console.log('[CastPanel] Initial cast state:', initialState)

    // Cast is available as long as SDK initialized (devices may or may not be present)
    // The Cast button will show device picker which handles "no devices" case
    setCastAvailable(true)
    console.log('[CastPanel] Cast SDK initialized successfully')
  }

  const handleCastStateChange = (event: CastEvent) => {
    const castContext = window.cast?.framework?.CastContext.getInstance()
    const CastState = window.cast?.framework?.CastState
    if (!castContext || !CastState) return

    console.log('[CastPanel] Cast state changed:', event.castState)

    // Update castAvailable based on whether devices are present
    if (event.castState === CastState.NO_DEVICES_AVAILABLE) {
      console.log('[CastPanel] No cast devices available')
      // Keep castAvailable true - requestSession() will show "no devices" message
    }

    if (event.castState === CastState.CONNECTED) {
      const session = castContext.getCurrentSession()
      if (session) {
        const deviceName = session.getSessionObj()?.receiver?.friendlyName
        setCastDeviceName(deviceName || 'Chromecast')
      }
    } else if (event.castState === CastState.NOT_CONNECTED) {
      setCastDeviceName(null)
      setReceiverCapabilities(null)
      setReceiverState(null)
    }
  }

  const handleSessionStateChange = (event: CastEvent) => {
    const sessionStates = window.cast?.framework.SessionState
    if (!sessionStates) return

    if (event.sessionState === sessionStates.SESSION_STARTED ||
        event.sessionState === sessionStates.SESSION_RESUMED) {
      // Session started
      if (castMode === 'native') {
        setupNativeReceiver()
      } else {
        loadCompositeMedia()
      }
    } else if (event.sessionState === sessionStates.SESSION_ENDED) {
      // Session ended
      cleanupSession()
      stopCastSession()
    }
  }

  /**
   * Set up native receiver (Phase 2) with custom messaging
   */
  const setupNativeReceiver = () => {
    const castContext = window.cast?.framework?.CastContext.getInstance()
    const session = castContext?.getCurrentSession()

    if (!session) {
      console.error('[CastPanel] No session for native receiver setup')
      return
    }

    // Set up message listener
    const messageListener = (namespace: string, message: string) => {
      handleReceiverMessage(namespace, message)
    }
    messageListenerRef.current = messageListener
    session.addMessageListener(OBA_NAMESPACE, messageListener)

    // Start heartbeat
    startHeartbeat()

    setCastState('connecting')
    console.log('[CastPanel] Native receiver setup complete, waiting for READY')
  }

  /**
   * Handle messages from the receiver
   */
  const handleReceiverMessage = (namespace: string, messageStr: string) => {
    if (namespace !== OBA_NAMESPACE) return

    try {
      const message = JSON.parse(messageStr)
      console.log('[CastPanel] Receiver message:', message.type, message.payload)

      switch (message.type) {
        case ResponseType.READY:
          handleReceiverReady(message.payload)
          break

        case ResponseType.STATE_UPDATE:
          handleStateUpdate(message.payload)
          break

        case ResponseType.ACK:
          console.log('[CastPanel] ACK:', message.payload?.action)
          break

        case ResponseType.ERROR:
          console.error('[CastPanel] Receiver error:', message.payload)
          setCastError(message.payload?.message || 'Receiver error')
          break

        case ResponseType.FALLBACK_ACTIVATED:
          console.log('[CastPanel] Fallback activated:', message.payload?.reason)
          setCastMode('composite')
          break

        case ResponseType.PONG:
          // Heartbeat response received
          break

        default:
          console.log('[CastPanel] Unknown message type:', message.type)
      }
    } catch (error) {
      console.error('[CastPanel] Failed to parse receiver message:', error)
    }
  }

  /**
   * Handle READY message from receiver
   */
  const handleReceiverReady = (payload: { version: string; capabilities: ReceiverCapabilities }) => {
    console.log('[CastPanel] Receiver ready:', payload)
    setReceiverCapabilities(payload.capabilities)

    // Send initial scene
    sendSceneLoad()

    setCastState('connected')
  }

  /**
   * Handle STATE_UPDATE from receiver
   */
  const handleStateUpdate = (payload: { receiverState: string; layout: string; tiles: unknown[] }) => {
    setReceiverState(payload.receiverState)
  }

  /**
   * Send SCENE_LOAD message to receiver
   */
  const sendSceneLoad = useCallback(async () => {
    const castContext = window.cast?.framework?.CastContext.getInstance()
    const session = castContext?.getCurrentSession()

    if (!session) {
      console.error('[CastPanel] No session for scene load')
      return
    }

    // Determine if we should use composite fallback
    const shouldUseFallback =
      layout === 'quad' && (!receiverCapabilities?.nativeQuad || !receiverCapabilities)

    const message = {
      type: MessageType.SCENE_LOAD,
      payload: {
        layout: shouldUseFallback ? 'single' : layout,
        tiles: tiles.map(t => ({
          slot: t.slot,
          stream_id: t.channelId,
          stream_url: t.streamUrl.startsWith('http')
            ? t.streamUrl
            : `${window.location.origin}${t.streamUrl}`,
          channel_name: t.channelName,
          icon_url: t.iconUrl,
        })),
        active_audio_slot: activeAudioSlot,
        session_id: castSession?.id,
        // Composite fallback URL for Phase 1
        fallback_url: castSession?.playbackUrl
          ? `${window.location.origin}${castSession.playbackUrl}`
          : null,
      },
      timestamp: Date.now(),
    }

    try {
      await session.sendMessage(OBA_NAMESPACE, message)
      console.log('[CastPanel] SCENE_LOAD sent')
    } catch (error) {
      console.error('[CastPanel] Failed to send SCENE_LOAD:', error)
      // Fall back to composite mode
      loadCompositeMedia()
    }
  }, [tiles, layout, activeAudioSlot, castSession, receiverCapabilities])

  /**
   * Send SCENE_PATCH message to receiver
   */
  const sendScenePatch = useCallback(async () => {
    const castContext = window.cast?.framework?.CastContext.getInstance()
    const session = castContext?.getCurrentSession()

    if (!session || castMode !== 'native') return

    const message = {
      type: MessageType.SCENE_PATCH,
      payload: {
        layout,
        tiles: tiles.map(t => ({
          slot: t.slot,
          stream_id: t.channelId,
          stream_url: t.streamUrl.startsWith('http')
            ? t.streamUrl
            : `${window.location.origin}${t.streamUrl}`,
          channel_name: t.channelName,
          icon_url: t.iconUrl,
        })),
        active_audio_slot: activeAudioSlot,
      },
      timestamp: Date.now(),
    }

    try {
      await session.sendMessage(OBA_NAMESPACE, message)
    } catch (error) {
      console.error('[CastPanel] Failed to send SCENE_PATCH:', error)
    }
  }, [tiles, layout, activeAudioSlot, castMode])

  /**
   * Load composite media (Phase 1 fallback)
   */
  const loadCompositeMedia = async () => {
    const castContext = window.cast?.framework?.CastContext.getInstance()
    const session = castContext?.getCurrentSession()

    if (!session || !castSession?.playbackUrl) {
      console.error('[CastPanel] No session or playback URL for composite mode')
      return
    }

    try {
      const fullUrl = `${window.location.origin}${castSession.playbackUrl}`
      const mediaInfo = new window.chrome!.cast!.media.MediaInfo(fullUrl, 'application/x-mpegURL')
      const request = new window.chrome!.cast!.media.LoadRequest(mediaInfo)
      request.autoplay = true

      await session.loadMedia(request)
      setCastState('connected')
      setCastMode('composite')
    } catch (error) {
      console.error('[CastPanel] Failed to load composite media:', error)
      setCastError('Failed to load media on cast device')
    }
  }

  /**
   * Start heartbeat to receiver
   */
  const startHeartbeat = () => {
    if (heartbeatTimer.current) {
      clearInterval(heartbeatTimer.current)
    }

    heartbeatTimer.current = window.setInterval(() => {
      sendHeartbeat()
    }, 5000)
  }

  /**
   * Send heartbeat to receiver
   */
  const sendHeartbeat = async () => {
    const castContext = window.cast?.framework?.CastContext.getInstance()
    const session = castContext?.getCurrentSession()

    if (!session) return

    const message = {
      type: MessageType.HEARTBEAT,
      payload: { timestamp: Date.now() },
      timestamp: Date.now(),
    }

    try {
      await session.sendMessage(OBA_NAMESPACE, message)
    } catch {
      // Heartbeat failed, session may be disconnected
    }
  }

  /**
   * Clean up session resources
   */
  const cleanupSession = () => {
    if (heartbeatTimer.current) {
      clearInterval(heartbeatTimer.current)
      heartbeatTimer.current = null
    }

    // Remove message listener
    if (messageListenerRef.current) {
      const castContext = window.cast?.framework?.CastContext.getInstance()
      const session = castContext?.getCurrentSession()
      if (session) {
        session.removeMessageListener(OBA_NAMESPACE, messageListenerRef.current)
      }
      messageListenerRef.current = null
    }
  }

  /**
   * Handle Cast button click
   */
  const handleCastClick = async () => {
    if (tiles.length === 0) {
      setCastError('Select at least one channel to cast')
      return
    }

    if (castState === 'connected') {
      // Send STOP message before ending session
      const castContext = window.cast?.framework?.CastContext.getInstance()
      const session = castContext?.getCurrentSession()
      if (session && castMode === 'native') {
        try {
          await session.sendMessage(OBA_NAMESPACE, {
            type: MessageType.STOP,
            timestamp: Date.now(),
          })
        } catch {
          // Ignore
        }
      }

      cleanupSession()
      castContext?.endCurrentSession(true)
      await stopCastSession()
      return
    }

    // Start cast session (creates composite fallback URL)
    await startCastSession()

    // Request Chromecast session
    try {
      const castContext = window.cast?.framework?.CastContext.getInstance()
      await castContext?.requestSession()
    } catch (error) {
      console.error('[CastPanel] Failed to start cast session:', error)
      setCastError('Failed to connect to cast device')
    }
  }

  /**
   * Handle AirPlay button click (always uses composite stream)
   */
  const handleAirPlayClick = () => {
    if (tiles.length === 0) {
      setCastError('Select at least one channel to cast')
      return
    }

    // Start cast session to get the composite stream URL
    startCastSession().then(() => {
      // Show AirPlay picker
      if (videoRef.current && 'webkitShowPlaybackTargetPicker' in videoRef.current) {
        (videoRef.current as HTMLVideoElement & { webkitShowPlaybackTargetPicker: () => void }).webkitShowPlaybackTargetPicker()
      }
    })
  }

  const isLoading = castState === 'connecting'
  const isConnected = castState === 'connected'

  // Determine status badge
  const getStatusBadge = () => {
    if (!isConnected) return null

    if (castMode === 'composite') {
      return (
        <span className="text-xs text-yellow-400 hidden md:inline" title="Using composite stream (Phase 1)">
          <AlertCircle className="w-3 h-3 inline mr-1" />
          Composite
        </span>
      )
    }

    return null
  }

  // Debug: log render state
  console.log('[CastPanel] Render:', { castAvailable, airPlayAvailable, castState, isLoading, isConnected, isAndroid })

  // Handle Cast button click - try to cast even if SDK didn't detect devices
  const handleCastButtonClick = async () => {
    // On Android, Cast SDK doesn't work - direct to browser menu
    if (isAndroid) {
      setCastError('Use browser menu (⋮) → Cast')
      return
    }

    // If SDK is initialized, try to cast (device picker handles "no devices" case)
    if (window.cast?.framework && window.chrome?.cast) {
      // Make sure we're initialized
      if (!castInitialized.current) {
        initializeCast()
      }
      handleCastClick()
      return
    }

    // SDK not available
    setCastError('Cast not available. Try refreshing the page.')
  }

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {/* Cast button - always show */}
      <button
        onClick={handleCastButtonClick}
        disabled={isLoading}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
          isConnected
            ? 'bg-blue-600 text-white hover:bg-blue-700'
            : 'bg-sidebar-muted text-sidebar-foreground hover:bg-sidebar-muted/80'
        }`}
        title={isConnected ? 'Stop casting' : 'Cast to TV'}
      >
        {isLoading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Cast className="w-4 h-4" />
        )}
        <span className="hidden sm:inline">
          {isConnected ? 'Stop' : 'Cast'}
        </span>
      </button>

      {/* AirPlay button */}
      {airPlayAvailable && (
        <button
          onClick={handleAirPlayClick}
          disabled={isLoading}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-sidebar-muted text-sidebar-foreground hover:bg-sidebar-muted/80 transition-colors"
          title="AirPlay (uses composite stream)"
        >
          <Airplay className="w-4 h-4" />
          <span className="hidden sm:inline">AirPlay</span>
        </button>
      )}

      {/* Cast status */}
      {isConnected && castDeviceName && (
        <span className="text-xs text-sidebar-foreground/60 hidden md:inline">
          <Volume2 className="w-3 h-3 inline mr-1" />
          {castDeviceName}
        </span>
      )}

      {/* Mode badge */}
      {getStatusBadge()}

      {/* Error display */}
      {castError && (
        <div className="flex items-center gap-1 text-xs text-red-400">
          <span>{castError}</span>
          <button onClick={() => setCastError(null)} className="p-0.5 hover:bg-red-500/20 rounded">
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Hidden video for AirPlay */}
      <video
        ref={videoRef}
        style={{ display: 'none' }}
        playsInline
        // @ts-expect-error - webkit attribute for AirPlay
        webkitPlaysinline="true"
      />
    </div>
  )
}
